import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { getStreamNotificationsUrl } from '@/lib/api/generated/api';
import type {
  NotificationStreamEvent,
  RunThreadChangedEvent,
  WorkspaceChangedEvent,
} from '@/lib/api/generated/model';
// The tasks feature's leaf module, not its index: the index brings in
// `TaskDrawer`, which imports this feature back (see `deepLink.ts`).
import { isRunThreadQuery } from '@/features/tasks/links';
import { isAboutWorkspace } from '../workspaceQueries';
import { env } from '@/lib/env';

import { useAlertStore } from '../alerts/store';
import { useArrivalStore } from '../arrivals';
import { notificationKeys } from '../queryKeys';
import { reportNotificationProblem } from '../report';
import { holdEventStream } from '../stream';

/**
 * The push channel (service spec 116 US5), held once by the shell.
 *
 * Every notification message carries the unread count after it, which goes
 * straight into the badge; a `notification` or `notification_resolved`
 * message also refreshes the inbox lists (spec 135). A `workspace_changed` message refreshes that workspace's pages
 * (spec 132), and a `run_thread_changed` message that run's Conversation
 * (spec 149). The
 * stream is closed while the tab is hidden and reopened when it is shown,
 * which is when `ready` brings the count back up to date. This is the push
 * channel the constitution's Server State Freshness rule names: nothing here
 * polls.
 */
/** The events this client understands; anything else is reported, not guessed at. */
const KNOWN_EVENTS = new Set([
  'ready',
  'notification',
  'notification_resolved',
  // A bulk or whole-filter change (service spec 130): only the count.
  'inbox_changed',
]);

export function useNotificationStream(enabled = true): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) return undefined;
    let controller: AbortController | undefined;

    const open = () => {
      if (controller || document.hidden) return;
      const current = new AbortController();
      controller = current;
      void holdEventStream({
        url: `${env.VITE_API_BASE_URL}${getStreamNotificationsUrl()}`,
        signal: current.signal,
        onEvent: ({ event, data }) => {
          let message: unknown;
          try {
            message = JSON.parse(data);
          } catch (error) {
            // A message that is not JSON leaves this tab a step behind until
            // the next one; say so, with what arrived.
            reportNotificationProblem('unreadable stream message', {
              event,
              data: data.slice(0, 500),
              error: String(error),
            });
            return;
          }
          // Something in a workspace changed (spec 132): refresh whatever
          // this tab holds about it. Only queries on screen refetch; the
          // rest are marked stale for when they are next shown. This is
          // what keeps an idle board current without polling.
          if (event === 'workspace_changed') {
            const { workspace_id: workspaceId } =
              message as WorkspaceChangedEvent;
            if (typeof workspaceId !== 'string') {
              reportNotificationProblem(
                'workspace_changed without a workspace',
                {
                  data: data.slice(0, 500),
                },
              );
              return;
            }
            void queryClient.invalidateQueries({
              predicate: (query) =>
                isAboutWorkspace(query.queryKey, workspaceId),
            });
            return;
          }
          // Something on a run's thread changed (spec 149): a message, or a
          // reply's state. Only that run's Conversation re-reads, and only if
          // someone has it open; the rest are marked stale.
          if (event === 'run_thread_changed') {
            const { execution_id: executionId } =
              message as RunThreadChangedEvent;
            if (typeof executionId !== 'string') {
              reportNotificationProblem('run_thread_changed without a run', {
                data: data.slice(0, 500),
              });
              return;
            }
            void queryClient.invalidateQueries({
              predicate: (query) =>
                isRunThreadQuery(query.queryKey, executionId),
            });
            return;
          }
          if (!KNOWN_EVENTS.has(event)) {
            // A name the service added that this client does not know yet:
            // ignored, but not silently — it is how contract drift shows.
            reportNotificationProblem('unknown stream event', { event });
            return;
          }
          const {
            unread,
            alert,
            kind,
            notification,
            notification_id: notificationId,
          } = message as NotificationStreamEvent;
          if (typeof unread !== 'number') {
            reportNotificationProblem('stream message without a count', {
              event,
              data: data.slice(0, 500),
            });
            return;
          }
          queryClient.setQueryData(notificationKeys.count(), unread);
          // The service decided this one should pop up (service spec 121),
          // and sent it whole, so it shows without another request.
          if (event === 'notification' && alert && notification) {
            useAlertStore.getState().push(notification);
          }
          // Its cause is over: nothing left to act on (spec 136 US1).
          if (event === 'notification_resolved' && notificationId) {
            useAlertStore.getState().dismiss(notificationId);
          }
          // A new one goes to the notifications page as an arrival to
          // count, not a row to insert (spec 147 FR-007).
          if (event === 'notification' && notificationId) {
            useArrivalStore
              .getState()
              .push(notificationId, notification?.kind ?? kind ?? null);
          }
          // A resolved notification (spec 135) changes what the lists show —
          // a "Resolved" label, and gone from Needs you — like a new one. A
          // bulk change elsewhere (spec 147) changes them too.
          if (
            event === 'notification' ||
            event === 'notification_resolved' ||
            event === 'inbox_changed' ||
            event === 'ready'
          ) {
            void queryClient.invalidateQueries({
              queryKey: notificationKeys.lists(),
            });
          }
          // The page holds still for a new one, and changes in place for the
          // rest.
          if (event !== 'notification') {
            void queryClient.invalidateQueries({
              queryKey: notificationKeys.pages(),
            });
          }
        },
      });
    };
    const close = () => {
      controller?.abort();
      controller = undefined;
    };
    const onVisibility = () => {
      if (document.hidden) close();
      else open();
    };

    open();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      close();
    };
  }, [enabled, queryClient]);
}
